import { tileCompare, tileImages } from '@/lib/media';
import { HREFLANG, LOCALES, localePath, type Locale } from '@/lib/site';

// The blog's post registry. Each post has a localized URL slug per locale.
// The localized copy lives in messages under `blog.posts.<id>`. Publication
// date + card image stay here so they never drift between locales. Images
// come from the local media manifest — no external URLs.
export interface BlogPostMeta {
  /** Canonical id — the key of the post's copy in messages `blog.posts`. */
  id: string;
  date: string;
  /** Set when the article content/meta is meaningfully updated after publication
   * — feeds BlogPosting dateModified + the sitemap lastmod. */
  lastModified?: string;
  image: string;
  slugs: Record<Locale, string>;
  /** In-article images, inserted after the section with the given index. */
  inlineImages?: { afterSection: number; image: string; portrait?: boolean }[];
}

/** Author profile for BlogPosting author.url — Yurii's public TikTok. */
export const AUTHOR_URL = 'https://www.tiktok.com/@Yurii_Yat';

export const BLOG_POSTS: BlogPostMeta[] = [
  {
    id: 'umywalka-z-plytek-na-wymiar',
    date: '2026-08-02',
    lastModified: '2026-08-04',
    image: tileCompare.after,
    slugs: {
      pl: 'umywalka-z-plytek-na-wymiar',
      en: 'custom-tiled-washbasin',
      ua: 'umyvalnyk-z-plytky-na-zamovlennya',
      ru: 'rakovina-iz-plitki-na-zakaz',
    },
  },
  {
    id: 'odbior-lazienki-po-remoncie-lista-kontrolna',
    date: '2026-07-14',
    image: tileImages[23],
    // Walk-in shower with a linear drain — sits right after the slope-check
    // section, which is about exactly that.
    inlineImages: [{ afterSection: 2, image: tileImages[13], portrait: true }],
    slugs: {
      pl: 'odbior-lazienki-po-remoncie-lista-kontrolna',
      en: 'bathroom-renovation-acceptance-checklist',
      ua: 'pryimannya-vannoyi-pislya-remontu',
      ru: 'priemka-vannoy-posle-remonta',
    },
  },
  {
    id: 'pekniecie-wanny-co-zrobic',
    date: '2026-06-26',
    image: tileImages[0],
    slugs: {
      pl: 'pekniecie-wanny-co-zrobic',
      en: 'cracked-bathtub-repair-or-replace',
      ua: 'trisnula-vanna-shcho-robyty',
      ru: 'treshchina-v-vanne-chto-delat',
    },
  },
];

export const getPostByLocaleSlug = (locale: Locale, slug: string) =>
  BLOG_POSTS.find((post) => post.slugs[locale] === slug);

/** Match a slug from ANY locale — used to 301 e.g. /en/blog/<pl-slug> to the en slug. */
export const getPostByAnySlug = (slug: string) =>
  BLOG_POSTS.find((post) => Object.values(post.slugs).includes(slug));

/**
 * "Read also" picks: every post except the current one, in a pseudo-random
 * order seeded by the current post's id. Deterministic on purpose — the pages
 * are statically rendered, and a Math.random() pick would either freeze at
 * build time or flip visibly after hydration.
 */
export function getRelatedPosts(currentId: string, count = 2): BlogPostMeta[] {
  const hash = (s: string) => {
    let h = 5381;
    for (const c of s) h = (h * 33) ^ c.charCodeAt(0);
    return h >>> 0;
  };
  return BLOG_POSTS.filter((post) => post.id !== currentId)
    .sort((a, b) => hash(currentId + a.id) - hash(currentId + b.id))
    .slice(0, count);
}

/**
 * hreflang alternates for one post. Slugs differ per locale, so this is built
 * here instead of via site.ts altLanguages() (which assumes a shared suffix).
 */
export function postAltLanguages(post: BlogPostMeta): Record<string, string> {
  const path = (locale: Locale) => `${localePath(locale)}/blog/${post.slugs[locale]}`;
  const languages: Record<string, string> = { 'x-default': path('pl') };
  for (const locale of LOCALES) languages[HREFLANG[locale]] = path(locale);
  return languages;
}

// Localized "18 lipca 2026" date. `ua` is this site's segment for Ukrainian;
// map it to the BCP-47 tag `uk` so Intl resolves it.
export function formatPostDate(iso: string, locale: string) {
  const intlLocale = locale === 'ua' ? 'uk' : locale;
  return new Intl.DateTimeFormat(intlLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${iso}T00:00:00`));
}
